import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  mergeClasses,
  Option,
  OptionOnSelectData,
  SelectionEvents,
  Spinner,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { Constants } from "common/constants";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { IUser } from "models/IUser";
import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
  useSharedDropdownStyles,
} from "theme/sharedStyles";
import {
  isNullUndefinedOrEmpty,
  mapAzureUsersToModel,
  mapAzureUserToModel,
} from "utils/generalUtils";
import { addUserToGroup, removeUserFromGroup } from "../../../api";
import { setUsers, updateUser } from "../../../redux/users";
import { useStyles } from "./EditDialog.styles";
import { IEditDialogProps } from "./EditDialog.types";

const ROLE_OPTIONS = [
  {
    key: Constants.ADMINS_GROUP_NAME_KEY,
    text: Constants.ADMINS_GROUP_NAME_VALUE,
  },
  {
    key: Constants.PLATFORM_ADMINS_GROUP_NAME_KEY,
    text: Constants.PLATFORM_ADMINS_GROUP_NAME_VALUE,
  },
  {
    key: Constants.DECOODA_ADMINS_GROUP_NAME_KEY,
    text: Constants.DECOODA_ADMINS_GROUP_NAME_VALUE,
  },
  {
    key: Constants.DIAGNOSTICS_ADMINS_GROUP_NAME_KEY,
    text: Constants.DIAGNOSTICS_ADMINS_GROUP_NAME_VALUE,
  },
  {
    key: Constants.MCP_USER_GROUP_NAME_KEY,
    text: Constants.MCP_USER_GROUP_NAME_VALUE,
  },
  {
    key: Constants.AI_METRICS_USERS_GROUP_NAME_KEY,
    text: Constants.AI_METRICS_USERS_GROUP_NAME_VALUE,
  },
  {
    key: Constants.AI_METRICS_ADMINS_GROUP_NAME_KEY,
    text: Constants.AI_METRICS_ADMINS_GROUP_NAME_VALUE,
  },
  {
    key: Constants.PROJECT_IQ_ADMINS_GROUP_NAME_KEY,
    text: "Project IQ Admin",
  },
];

export const EditDialog = (props: IEditDialogProps): JSX.Element => {
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();
  const dropdownStyles = useSharedDropdownStyles();
  const styles = useStyles();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const [selectedUsers, setSelectedUsers] = useState<CustomOption[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<IUser[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const dispatch = useDispatch();

  const users: IUser[] = useSelector(
    (state: RootState) => state.usersSlice.value,
  );

  useEffect((): void => {
    if (props.user) {
      const selectedUser: CustomOption = mapAzureUserToModel(props.user);
      setSelectedUsers([selectedUser]);

      const initialRoles = props.user.groups?.filter((g) => g !== "user") || [];
      setSelectedRoles(initialRoles);
    } else {
      setSelectedUsers([]);
      setSelectedRoles([]);
      setFilteredUsers(filterUsersByRoles([]));
    }
  }, [props.user]);

  const filterUsersByRoles = (roles: string[]) => {
    return users.filter(
      (user: IUser): boolean =>
        !roles.some((role: string): boolean | undefined =>
          user.groups?.includes(role),
        ),
    );
  };

  const updateUserRoles = async (
    currentUser: IUser,
    selectedRoles: string[],
  ) => {
    const currentRoles: string[] = currentUser.groups || [];
    let hasChanges: boolean = false;

    for (const role of currentRoles) {
      if (!selectedRoles.includes(role) && role !== "user") {
        hasChanges = true;
        await removeUserFromGroup(role, currentUser.id);
      }
    }

    for (const role of selectedRoles) {
      if (!currentRoles.includes(role)) {
        hasChanges = true;
        await addUserToGroup(role, currentUser.id);
      }
    }

    return hasChanges;
  };

  const onRoleChange = (event: SelectionEvents, data: OptionOnSelectData) => {
    const newRole = data.optionValue;
    if (!newRole) return;

    setSelectedRoles((prevSelectedRoles) => {
      let updatedRoles;
      if (prevSelectedRoles.includes(newRole)) {
        updatedRoles = prevSelectedRoles.filter(
          (role: string): boolean => role !== newRole,
        );
      } else {
        updatedRoles = [...prevSelectedRoles, newRole];
      }

      const filtered: IUser[] = filterUsersByRoles(updatedRoles);
      setFilteredUsers(filtered);
      return updatedRoles;
    });

    if (fieldErrors.role) {
      setFieldErrors((prev) => ({ ...prev, role: "" }));
    }
  };

  const handleSelectedUsers = (selectedOptions: CustomOption[]): void => {
    setSelectedUsers(selectedOptions);
    if (fieldErrors.user) {
      setFieldErrors((prev) => ({ ...prev, user: "" }));
    }
  };

  const validateFields = (): boolean => {
    const errors: Record<string, string> = {};

    if (selectedUsers.length === 0) {
      errors.user = "At least one user must be selected.";
    }

    if (selectedRoles.length === 0) {
      errors.role = "At least one role must be selected.";
    }

    const invalidUsers = selectedUsers.filter(
      (user: CustomOption) =>
        !(user as any).groups?.includes(Constants.MEMBERS_GROUP_NAME_KEY),
    );

    if (invalidUsers.length > 0) {
      errors.general = `The following users are not members of the required group: ${invalidUsers.map((u: CustomOption) => (u as any).displayName).join(", ")}`;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    setFieldErrors({});
    return true;
  };

  const handleSave = async (ev: React.FormEvent): Promise<void> => {
    ev.preventDefault();

    if (!validateFields()) {
      return;
    }

    setIsLoading(true);

    try {
      const rolesWithMembers = selectedRoles.includes(
        Constants.MEMBERS_GROUP_NAME_KEY,
      )
        ? selectedRoles
        : [...selectedRoles, Constants.MEMBERS_GROUP_NAME_KEY];

      const updatePromises: Promise<void>[] = [];

      for (const option of selectedUsers) {
        const raw = option as any;
        const user: IUser = {
          id: raw?.id,
          userPrincipalName: raw?.upn,
          displayName: raw?.displayName,
          mail: raw?.mail,
          groups: raw?.groups,
        };

        const promise = updateUserRoles(user, rolesWithMembers).then(
          async (hasChanges) => {
            if (hasChanges) {
              const updatedUser: IUser = { ...user, groups: rolesWithMembers };

              const isOnlyMember: boolean =
                rolesWithMembers.length === 1 &&
                rolesWithMembers[0] === Constants.MEMBERS_GROUP_NAME_KEY;

              if (isOnlyMember) {
                dispatch(setUsers(users.filter((u) => u.id !== user.id)));
              } else {
                const existing = users.find((u) => u.id === user.id);
                if (existing) {
                  dispatch(updateUser(updatedUser));
                } else {
                  dispatch(setUsers([...users, updatedUser]));
                }
              }
            }
          },
        );

        updatePromises.push(promise);
      }

      await Promise.all(updatePromises);

      setSelectedRoles([]);
      setSelectedUsers([]);
      props.onDismiss(true);
    } catch (error) {
      console.error("Error updating user roles:", error);
      setFieldErrors({ general: "Something went wrong while updating roles." });
    } finally {
      setIsLoading(false);
    }
  };

  const readResourcesForPickerFieldFiltered = async (
    searchedValue: string,
  ): Promise<any[]> => {
    return new Promise((resolve) => {
      const mappedUsers = mapAzureUsersToModel(filteredUsers);
      const filteredResources = mappedUsers.filter((resource: any) =>
        resource.Name?.toLowerCase().includes(searchedValue.toLowerCase()),
      );

      resolve(filteredResources);
    });
  };

  const hasIncompleteDetails =
    selectedUsers.length === 0 || selectedRoles.length === 0;

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) props.onDismiss(false);
      }}
    >
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={() => props.onDismiss(false)}
              />
            }
          >
            {props.user ? "Update User" : "Add User"}
          </DialogTitle>
          <DialogContent
            className={mergeClasses(
              contentStyles.formContainer,
              styles.formContent,
            )}
          >
            {isLoading && (
              <div className={contentStyles.formLoadingOverlay}>
                <Spinner label="Updating user group..." />
              </div>
            )}
            <Field
              label="User"
              required
              validationState={fieldErrors.user ? "error" : "none"}
              validationMessage={fieldErrors.user}
            >
              <PeoplePicker
                id="user"
                handleSelectedItems={handleSelectedUsers}
                disabled={!isNullUndefinedOrEmpty(props.user) || isLoading}
                required={true}
                readResourcesForPickerFieldFiltered={
                  readResourcesForPickerFieldFiltered
                }
                selectedOptions={selectedUsers}
              />
            </Field>

            <Field
              label="Role"
              required
              validationState={fieldErrors.role ? "error" : "none"}
              validationMessage={fieldErrors.role}
            >
              <Dropdown
                multiselect={true}
                id={"user-role"}
                placeholder="Select a role..."
                value={
                  selectedRoles.length > 0
                    ? selectedRoles.join(", ")
                    : undefined
                }
                selectedOptions={selectedRoles}
                onOptionSelect={onRoleChange}
                listbox={{ className: dropdownStyles.listbox }}
                positioning="below"
                disabled={isLoading}
              >
                {ROLE_OPTIONS.map(
                  (option: { key: string; text: string }): JSX.Element => (
                    <Option
                      key={option.key.toString()}
                      value={option.key.toString()}
                    >
                      {option.text}
                    </Option>
                  ),
                )}
              </Dropdown>
            </Field>

            {fieldErrors.general && (
              <div role="alert" className={contentStyles.feedbackError}>
                {fieldErrors.general}
              </div>
            )}
          </DialogContent>
        </DialogBody>
        {!isLoading && (
          <div className={footerStyles.dialogFooter}>
            <DialogFooterAction
              icon={<CheckmarkRegular />}
              onClick={handleSave}
              dimmed={hasIncompleteDetails}
              tooltip="Save"
            />
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
